import { Linkedin, Instagram, Github, Music4, Link2 } from "lucide-react"
import { Text, Box, Image, Progress, Flex, Link, IconButton } from "@chakra-ui/react"
import LiquidGlassBox from "../components/ui/LiquidGlassBox"
import LiquidGlassBoxStatic from "../components/ui/LiquidGlassBoxStatic"
import "./Home.css"
import { useState, useRef, useEffect } from "react"
import { Spinner } from "@chakra-ui/react"
import { KoltBot } from "../components/koltBot"

function msToMinutesSeconds(ms) {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export function Home() {

    const [songName, setSongName] = useState("")
    const [artistName, setArtistName] = useState("")
    const [imageUrl, setImageUrl] = useState("")
    const [songIsLoading, setSongIsLoading] = useState(true)
    const [songDuration, setSongDuration] = useState(0)
    const [songProgress, setSongProgress] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)

    const retryCount = useRef(0)
    const maxRetries = 5

    useState(() => {
        const fetchSongData = async () => {
            setSongIsLoading(true)
            try {
                const response = await fetch("https://txz4mvpkqg.execute-api.us-east-2.amazonaws.com/dev/spotifyGetCurrentSong")
                const data = await response.json()
                console.log("Fetched song data:", data)
                
                // Check if a song is actually playing
                if (data.name && data.name.trim() !== "") {
                    setSongName(data.name)
                    setArtistName(data.artists)
                    setImageUrl(data.albumImage)
                    setSongDuration(data.durationMs)
                    setSongProgress(data.progressMs)
                    setIsPlaying(true)
                } else {
                    setIsPlaying(false)
                }
                retryCount.current = 0
            } catch (error) {
                if (retryCount.current < maxRetries) {
                    retryCount.current += 1
                    console.error("Error fetching song data refreshing tokns:", error)
                    const refreshResponse = await fetch("https://txz4mvpkqg.execute-api.us-east-2.amazonaws.com/dev/spotifyRefresh")
                    const data = await refreshResponse.json()
                    console.log("response from refresh: ", data)
                    fetchSongData()
                } else {
                    console.error("Max retries reached")
                    setIsPlaying(false)
                }
            } finally {
                setSongIsLoading(false)
            }
        }
        fetchSongData()
    }, [])

    return (
        <Box
            className = "background"
            display="flex"
            flexDirection="column"
        >
            <Box>
                <Box
                    className="header"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    fontWeight="bold"
                    height="64px"
                    position="sticky"
                    px={{ base: "4%", lg: "6%", xl: "100px" }}
                >
                    <LiquidGlassBox
                        display="flex"
                        justifyContent="center"
                        gap={{ base: 4, md: 6, lg: 10 }}
                        alignItems="center"
                        marginTop="50px"
                        padding={3}
                    >
                        <Text 
                            as="a" 
                            href="#koltbot" 
                            fontFamily={"body"} 
                            textShadow="0 0 8px #483AA0" 
                            fontSize={{ base: "md", lg: "xl" }}
                            marginRight={{ base: 2, md: 5 }}
                            marginLeft={{ base: 2, md: 5 }}
                            color="secondary"
                            cursor="pointer"
                            _hover={{ opacity: 0.8 }}
                        > 
                            KoltBot 
                        </Text>
                        <Text 
                            as="a" 
                            href="#projects" 
                            fontFamily={"body"} 
                            textShadow="0 0 8px #483AA0" 
                            fontSize={{ base: "md", lg: "xl" }}
                            marginRight={{ base: 2, md: 5 }}
                            color="secondary"
                            cursor="pointer"
                            _hover={{ opacity: 0.8 }}
                        > 
                            Projects 
                        </Text>
                    </LiquidGlassBox>
                    <LiquidGlassBox
                        marginTop="50px"
                        display="flex"
                        height="74px"
                        width={{ base: "auto", lg: "336px" }}
                        justifyContent="center"
                        alignItems="center"
                        padding={3}
                        gap={{ base: "30px", md: "40px", lg: "60px" }}
                    >
                        <Link href = "https://github.com/ColtonFRSTT">
                            <Github style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="2.2rem" />
                        </Link>
                        <Link href = "https://www.linkedin.com/in/colton-fridgen-74b838183/">
                            <Linkedin style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="2.2rem" />
                        </Link>
                        <Link>
                            <Instagram style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="2.2rem" />
                        </Link>
                    </LiquidGlassBox>
                </Box>
                <Box
                    id="home"
                    className="profile-section"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent = "center"
                    minHeight="calc(100vh - 64px)"
                >
                    <LiquidGlassBox 
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mt={{ base: "-100px", lg: "-200px" }}
                        height={{ base: "auto", md: "400px", lg: "500px" }}
                        width={{ base: "90%", md: "85%", lg: "80%", xl: "1200px" }}
                        maxWidth="1200px"
                        padding={{ base: 6, md: 8, lg: 10 }}
                        flexDirection={{ base: "column", md: "row" }}
                    >
                        <Image
                            src="/images/profPic.jpg"
                            boxSize={{ base: "180px", md: "220px", lg: "250px" }}
                            borderRadius="full"
                            fit="cover"
                            alt="Profile"
                            mt={{ base: 0, md: "-20px" }}
                            ml={{ base: 0, md: "-50px", lg: "-150px" }}
                        />
                        <Box
                            display="flex"
                            flexDirection="column"
                            ml={{ base: 0, md: "30px" }}
                            mt={{ base: 4, md: "-80px" }}
                            alignItems={{ base: "center", md: "flex-start" }}
                        >
                            <Text ml={{ base: 0, md: 2 }} fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={{ base: "xl", md: "xxl", lg: "xxxl" }} color={"secondary"} textAlign={{ base: "center", md: "left" }}>
                                Colton Fridgen
                            </Text>
                            <Text ml={{ base: 0, md: 5 }} mb={2} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"} textAlign={{ base: "center", md: "left" }}>
                                Software Developer
                            </Text>
                            <Box
                                as="a"
                                href="/images/defaultResume.pdf"
                                download="Colton_Fridgen_Resume.pdf"
                                display="flex"
                                alignItems="center"
                                gap="8px"
                                px={4}
                                width="205px"
                                py={2}
                                ml={{ base: 0, md: 4 }}
                                mt={6}
                                border="1px solid rgba(255,255,255,.35)"
                                borderRadius="20px"
                                textDecoration="none"
                                transition="all 0.2s ease"
                                _hover={{ transform: "translateY(-1px)", boxShadow: "0bbg 0 8px #483AA0" }}
                            >
                                <Link2 style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="1.1rem" />
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} color={"secondary"}>
                                    Download Resume
                                </Text>
                            </Box>

                        </Box>
                    </LiquidGlassBox>
                </Box>
                <Box
                    id="koltbot"
                    className="content"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    minHeight="calc(100vh - 64px)"
                    bg="transparent"
                    pt={10}
                    pb={10}
                >
                    <Box
                        display = "flex"
                        flexDirection = "column"
                        width={{ base: "95%", md: "90%", lg: "85%", xl: "1300px" }}
                        maxWidth="1300px"
                        px={{ base: 4, md: 0 }}
                    >
                        <Text ml={2} fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxl"} color={"secondary"}>
                            Ask KoltBot
                        </Text>
                        <Text ml={2} mb={2} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"}>
                            KoltBot is designed to answer questions about my projects, work experience, hobbies, and more
                        </Text>
                        <LiquidGlassBoxStatic>
                            <KoltBot />
                        </LiquidGlassBoxStatic>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        position="fixed"
                        bottom="30px"
                        right={{ base: "20px", md: "50px", lg: "100px" }}
                        zIndex={20}
                    >
                        <LiquidGlassBox
                            className="liquid-glass"
                            display="flex"
                            flexDirection="column"
                            padding={4}
                            borderRadius="md"
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                mb={3}
                            >
                                <Music4 style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color = "#483AA0"></Music4>
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" color = "secondary" ml={2} mr={2} fontSize={"lg"} fontWeight={"Bold"}>Now Playing</Text>
                                <Music4 style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color = "#483AA0"></Music4>
                            </Box>

                            {(songIsLoading) && (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                    <Spinner color = "secondary"></Spinner>
                                </Box>
                            )}
                            {(!songIsLoading && !isPlaying) && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    minHeight="100px"
                                    opacity={0.7}
                                >
                                    <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize="md" color="gray.400" fontStyle="italic">
                                        Nothing playing right now
                                    </Text>
                                    <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize="xs" color="gray.500" mt={1}>
                                        Check back later to see what I'm listening to
                                    </Text>
                                </Box>
                            )}
                            {(!songIsLoading && isPlaying) && (
                                <>
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                >
                                    <Image
                                        src={imageUrl}
                                        alt="Current Song"
                                        boxSize="90px"
                                        borderRadius="md"
                                        objectFit="cover"
                                        fallbackSrc="https://via.placeholder.com/200" // Placeholder image while loading
                                    />
                                    <Box ml={3}>
                                        <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontWeight="bold" fontSize="md">{songName}</Text>
                                        <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize="sm"> By: {artistName}</Text>
                                        <Progress.Root max = {songDuration} min={0} value={songProgress} shape={"rounded"} size = {"sm"} mt={2}>
                                            <Progress.Track>
                                                <Progress.Range/>
                                            </Progress.Track>
                                        </Progress.Root>
                                        <Box
                                            display="flex"
                                            gap="250px"
                                        >
                                            <Text mt={1} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xs"}>{msToMinutesSeconds(songProgress)}</Text>
                                            <Text mt={1} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xs"}>{msToMinutesSeconds(songDuration)}</Text>
                                        </Box>
                                    </Box>
                                </Box>
                                </>
                            )}
                        </LiquidGlassBox>
                        <Box display="flex" justifyContent="flex-end">
                            <Text color = "secondary" fontSize="xs">Powered By Spotify</Text>
                        </Box>
                    </Box>

                    {/* Add extra content to enable scrolling */}
                </Box>
                <Box 
                    id="projects"
                    minHeight="100vh"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    py={20}
                    px={{ base: 4, md: 8, lg: 12 }}
                >
                    <Text fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxxl"} color={"secondary"}>
                        Projects
                    </Text>
                    <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"}>
                        Check out my projects
                    </Text>
                    <Flex 
                        gap={{ base: 8, md: 12, xl: 200 }}
                        mt={20}
                        flexWrap="wrap"
                        justifyContent="center"
                        width="100%"
                        maxWidth="1600px"
                    >
                        <LiquidGlassBox width={{ base: "100%", md: "45%", xl: "600px" }} maxWidth="600px" height="420px">
                            <Box
                                padding = {5}
                            >
                                <Text fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxl"} color={"secondary"} textAlign="center">
                                    MyKeen - Co-op
                                </Text>
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} textAlign="center">
                                    Jan. - Aug. 2025
                                </Text>
                                <Text height = {"150px"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"} textAlign="center" padding = {3} marginTop = {6}>
                                    A client service platform that leverages artificial intelligence and industry-informed automations to combine professional analysis, business insights with workflow automations. 
                                </Text>
                                <Box display="flex" justifyContent="center" mt={12}>
                                    <Box
                                        as="a"
                                        href="https://github.com/your-username/mykeen" // TODO: replace with actual repo URL
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        display="flex"
                                        alignItems="center"
                                        gap="8px"
                                        px={4}
                                        py={2}
                                        border="1px solid rgba(255,255,255,.35)"
                                        borderRadius="20px"
                                        textDecoration="none"
                                        transition="all 0.2s ease"
                                        _hover={{ transform: "translateY(-1px)", boxShadow: "0 0 8px #483AA0" }}
                                    >
                                        <Link href = "https://www.keenbusiness.ca/mykeen">
                                            <Link2 style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="1.1rem" />
                                            <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} color={"secondary"}>
                                                View Website
                                            </Text>
                                        </Link>
                                    </Box>
                                </Box>
                            </Box>
                        </LiquidGlassBox>
                        <LiquidGlassBox width={{ base: "100%", md: "45%", xl: "600px" }} maxWidth="600px" height="420px">
                            <Box
                                padding = {5}
                            >
                                <Text fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxl"} color={"secondary"} textAlign="center">
                                    Mun Course Notifier
                                </Text>
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} textAlign="center">
                                    Aug. - Sep. 2024
                                </Text>
                                <Text height = {"150px"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"} textAlign="center" padding = {3} marginTop = {6}>
                                    A fully automated platform that continuously monitors university course availability and notifies students via email when seats open.
                                </Text>
                                <Box display="flex" justifyContent="center" mt={12}>
                                    <Box
                                        as="a"
                                        href="https://github.com/your-username/mykeen" // TODO: replace with actual repo URL
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        display="flex"
                                        alignItems="center"
                                        gap="8px"
                                        px={4}
                                        py={2}
                                        border="1px solid rgba(255,255,255,.35)"
                                        borderRadius="20px"
                                        textDecoration="none"
                                        transition="all 0.2s ease"
                                        _hover={{ transform: "translateY(-1px)", boxShadow: "0 0 8px #483AA0" }}
                                    >
                                        <Link href = "https://github.com/ColtonFRSTT/course-notifier">
                                            <Github style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="1.1rem" />
                                            <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} color={"secondary"}>
                                                View on GitHub
                                            </Text>
                                        </Link>
                                    </Box>
                                </Box>
                            </Box>
                        </LiquidGlassBox>
                    </Flex>
                    <Flex 
                        gap={{ base: 8, md: 12, xl: 200 }}
                        mt={20}
                        flexWrap="wrap"
                        justifyContent="center"
                        width="100%"
                        maxWidth="1600px"
                    >
                        <LiquidGlassBox width={{ base: "100%", md: "45%", xl: "600px" }} maxWidth="600px" height="420px">
                            <Box
                                padding = {5}
                            >
                                <Text fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxl"} color={"secondary"} textAlign="center">
                                    Redline Frame
                                </Text>
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} textAlign="center">
                                    Sep. - Dec. 2025
                                </Text>
                                <Text height = {"150px"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"} textAlign="center" padding = {3} marginTop = {6}>
                                    A fast-paced 2D mech platformer built in C++ with SFML, featuring a custom ECS engine, energy-based movement, and responsive combat.
                                </Text>
                                <Box display="flex" justifyContent="center" mt={12}>
                                    <Box
                                        as="a"
                                        href="https://github.com/your-username/mykeen" // TODO: replace with actual repo URL
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        display="flex"
                                        alignItems="center"
                                        gap="8px"
                                        px={4}
                                        py={2}
                                        border="1px solid rgba(255,255,255,.35)"
                                        borderRadius="20px"
                                        textDecoration="none"
                                        transition="all 0.2s ease"
                                        _hover={{ transform: "translateY(-1px)", boxShadow: "0 0 8px #483AA0" }}
                                    >
                                        <Link href = "https://www.youtube.com/watch?v=c622pSpsjBI">
                                            <Link2 style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="1.1rem" />
                                            <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} color={"secondary"}>
                                                Watch Overview
                                            </Text>
                                        </Link>
                                    </Box>
                                </Box>
                            </Box>
                        </LiquidGlassBox>
                        <LiquidGlassBox width={{ base: "100%", md: "45%", xl: "600px" }} maxWidth="600px" height="420px">
                            <Box
                                padding = {5}
                            >
                                <Text fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxl"} color={"secondary"} textAlign="center">
                                    Resettlements DB
                                </Text>
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} textAlign="center">
                                    Sep. - Dec. 2025
                                </Text>
                                <Text height = {"150px"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"} textAlign="center" padding = {3} marginTop = {6}>
                                    AWS RDS-backed relational database modeling Newfoundland resettlements, capturing communities, structures, events, and outcomes through normalized schema design.
                                </Text>
                                <Box display="flex" justifyContent="center" mt={12}>
                                    <Box
                                        as="a"
                                        href="https://github.com/your-username/mykeen" // TODO: replace with actual repo URL
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        display="flex"
                                        alignItems="center"
                                        gap="8px"
                                        px={4}
                                        py={2}
                                        border="1px solid rgba(255,255,255,.35)"
                                        borderRadius="20px"
                                        textDecoration="none"
                                        transition="all 0.2s ease"
                                        _hover={{ transform: "translateY(-1px)", boxShadow: "0 0 8px #483AA0" }}
                                    >
                                        <Link href = "https://github.com/DeepPatel21313/communities-nl-database">
                                            <Github style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="1.1rem" />
                                            <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} color={"secondary"}>
                                                View on GitHub
                                            </Text>
                                        </Link>
                                    </Box>
                                </Box>
                            </Box>
                        </LiquidGlassBox>
                    </Flex>
                    <Flex 
                        gap={{ base: 8, md: 12, xl: 200 }}
                        mt={20}
                        flexWrap="wrap"
                        justifyContent="center"
                        width="100%"
                        maxWidth="1600px"
                    >
                        <LiquidGlassBox width={{ base: "100%", md: "45%", xl: "600px" }} maxWidth="600px" height="420px">
                            <Box
                                padding = {5}
                            >
                                <Text fontWeight={"bold"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"xxl"} color={"secondary"} textAlign="center">
                                    KoltBot
                                </Text>
                                <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} textAlign="center">
                                    Jun. 2025 - Present
                                </Text>
                                <Text height = {"150px"} fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"lg"} textAlign="center" padding = {3} marginTop = {6}>
                                    KoltBot is a Claude-powered chat bot that pulls code from my GitHub projects to generate clear, detailed explanations of how they work.
                                </Text>
                                <Box display="flex" justifyContent="center" mt={12}>
                                    <Box
                                        as="a"
                                        href="https://github.com/your-username/mykeen" // TODO: replace with actual repo URL
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        display="flex"
                                        alignItems="center"
                                        gap="8px"
                                        px={4}
                                        py={2}
                                        border="1px solid rgba(255,255,255,.35)"
                                        borderRadius="20px"
                                        textDecoration="none"
                                        transition="all 0.2s ease"
                                        _hover={{ transform: "translateY(-1px)", boxShadow: "0 0 8px #483AA0" }}
                                    >
                                        <Link href = "https://github.com/ColtonFRSTT/portfolio-website">
                                            <Github style={{ filter: "drop-shadow(0 0 8px #483AA0)"}} color="#483AA0" size="1.1rem" />
                                            <Text fontFamily={"body"} textShadow="0 0 8px #483AA0" fontSize={"md"} color={"secondary"}>
                                                View on GitHub
                                            </Text>
                                        </Link>
                                    </Box>
                                </Box>
                            </Box>
                        </LiquidGlassBox>
                    </Flex>
                </Box>
            </Box>
        </Box>
    )
}